import React, { useContext, useEffect, useState } from 'react';
import { AmplifyAuthContext, AmplifyAuthSignoutContext } from '../App';
import clsx from 'clsx';
import {
  createStyles,
  makeStyles,
  useTheme,
  Theme,
} from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import { API, Auth } from 'aws-amplify';
import { getAPIName } from '../config';
import { IconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApplicationRouter } from './routing.component';
import { NavMenuItem } from './NavItem';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Location } from '@reach/router';
import { List, Collapse, Divider, ListItem, ListItemText, ListItemIcon } from '@material-ui/core';
import { ExpandMore, ExpandLess, MoreHoriz } from '@material-ui/icons';
import { navigate } from '@reach/router';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faCar,
  faChartLine,
  faCog,
  faFile,
  faFolder,
  faHome,
  faLayerGroup,
  faListUl,
  faQuestion,
  faRoad,
  faUserTie,
  faUsers,
  faWarehouse,
  faCreditCard,
  faMagnifyingGlassDollar,
  faFileInvoiceDollar,
  faParking,
  faGear,
  faClipboardList,
  faRightFromBracket,
  faCarTunnel,
  faRoute
} from '@fortawesome/free-solid-svg-icons';
library.add(
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faCar,
  faChartLine,
  faCog,
  faFile,
  faFolder,
  faHome,
  faLayerGroup,
  faListUl,
  faQuestion,
  faRoad,
  faUserTie,
  faUsers,
  faWarehouse,
  faFileInvoiceDollar,
  faParking,
  faGear,
  faClipboardList,
  faRightFromBracket,
  faCarTunnel,
  faRoute
);

const drawerWidth = 240;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    },
    appBar: {
      backgroundColor: '#111218',
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      flexGrow: 1,
    },
    title: {
      flexGrow: 1,
      textTransform: 'uppercase',
      fontSize: '0.9rem',
      textAlign: 'left',
      gap: theme.spacing(1),
      marginBottom:5
    },
    appBarShift: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    menuButton: {
      marginRight: 36,
    },
    hide: {
      display: 'none',
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    drawerOpen: {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: theme.spacing(7) + 1,
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9) + 1,
      },
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    contactUsButton: {
      paddingRight: '0'
    },
    signOutButton: {
      paddingLeft: '0',
      width: '20px'
    }
  })
);

export default function MiniDrawer() {
  const classes = useStyles();
  const theme = useTheme();
  const currentAuthenticatedUser: any = useContext(AmplifyAuthContext);
  const amplifyAuthSignout: any = useContext(AmplifyAuthSignoutContext);
  
  const [open, setOpen] = React.useState(true);

  function handleDrawerOpen() {
    setOpen(true);
  }

  function handleDrawerClose() {
    setOpen(false);
  }

  const signOut = async (): Promise<void> => {
    await Auth.signOut();
    amplifyAuthSignout();
  };
  const [sidebarCollapse, setSidebarCollapse] = useState<boolean>(false);
  const toggleSidebarCollapse = () => setSidebarCollapse(!sidebarCollapse);
  const userRole: string =
    currentAuthenticatedUser == null ||
      currentAuthenticatedUser.attributes == null ||
      currentAuthenticatedUser.attributes.given_name == null
      ? 'Individual'
      : currentAuthenticatedUser.attributes.given_name;

  const [dealershipOrGroupName, setDealershipOrGroupName] = useState<string | null>('');

  useEffect(() => {
    const { given_name: given, family_name: family } = currentAuthenticatedUser?.attributes || {};
    if (!['Individual', 'Group'].includes(given) || !family) {
      setDealershipOrGroupName('');
      return;
    }
    API.get(getAPIName(), `${given === 'Group' ? '/dealershipgroups' : '/dealerships'}?id=${encodeURIComponent(family)}`, {})
      .then((res) => setDealershipOrGroupName(res?.body?.[0]?.name || ''))
      .catch(() => setDealershipOrGroupName(''));
  }, [ currentAuthenticatedUser?.attributes?.given_name, currentAuthenticatedUser?.attributes?.family_name,]);

  const given = currentAuthenticatedUser?.attributes?.given_name;
  const appBarSubtitle = given === 'Admin' ? '  -  Admin' : dealershipOrGroupName
        ? `  -  ${dealershipOrGroupName}`
        : '';

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, {
              [classes.hide]: open,
            })}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </IconButton>
          <Typography variant="h6" noWrap className={classes.title} >
            <img src="https://tollaid-emailtemplates.s3.us-east-2.amazonaws.com/DealershipLogos/logo-tollaid-white_red.png" alt="TollAid" height={30} width={65} />
            {appBarSubtitle}
          </Typography>
          <Button className={classes.contactUsButton} color="inherit" onClick={() => navigate('contact-us')}>
            Contact Us
          </Button>
          <Button className={classes.signOutButton} color="inherit" onClick={signOut}>
            <FontAwesomeIcon icon={faRightFromBracket} />
          </Button>
        </Toolbar>
      </AppBar>
    <Location>
      {({ location }) => (
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
        open={open}
      >
        <div className={classes.toolbar}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <FontAwesomeIcon icon={faAngleDoubleRight} />
            ) : (
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            )}
          </IconButton>
        </div>
        <Divider />
        <List>
          <NavMenuItem label="Dashboard" icon={faHome} route="" location={location}/>
          <NavMenuItem label="Contracts" icon={faListUl} route="contracts" location={location}/>
          <NavMenuItem label="Invoices" icon={faFile} route="invoices" location={location}/>
          <NavMenuItem label="Tolls" icon={faRoad} route="tolls" location={location}/>
          <NavMenuItem label="Authorize Payment" icon={faCreditCard} route="authorise" location={location}/>
          {/* Sidebar Collapse */}
          <Divider style={{ margin: '8px 0' }} />
          <ListItem button onClick={toggleSidebarCollapse}>
            <ListItemIcon><MoreHoriz /></ListItemIcon>
            <ListItemText primary={sidebarCollapse ? "Less" : "More"} />
            {sidebarCollapse ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          {/* Collapsible Section */}
          <Collapse in={sidebarCollapse} timeout="auto" unmountOnExit>
          <NavMenuItem label='ParkingTickets' icon={faParking} route="parkingtickets" location={location}/>
          {userRole === 'Admin' && (
            <NavMenuItem label="Toll Reports (Beta)" icon={faChartLine} route="tollreports" location={location}/>
          )}
          <NavMenuItem label="Financial Reports" icon={faFileInvoiceDollar}  route="financialreport" location={location}/>
          <NavMenuItem label="Vehicles" icon={faCar} route="vehicles" location={location}/>
          {userRole === 'Admin' && (<NavMenuItem label="Dealership Controls" icon={faGear} route="dealershipcontrols" location={location} /> )}
          {/*<NavMenuItem label="Employees" icon={faUserTie} route="employees" />*/}
          {userRole === 'Admin' && (
            <NavMenuItem
              label="Groups"
              icon={faLayerGroup}
              route="dealership-groups"
              location={location}
            />
          )}
          {(userRole === 'Admin' || userRole === 'Group') && (
            <React.Fragment>
              <NavMenuItem
                label="Locations"
                icon={faWarehouse}
                route="dealerships"
                location={location}
              />

              <NavMenuItem label="Users" icon={faUsers} route="users" location={location}/>
            </React.Fragment>
          )}
          {userRole === 'Admin' && (
            <NavMenuItem
              label="TollRates"
              icon={faMagnifyingGlassDollar}
              route="tollrates"
              location={location}
            />
          )}
            <NavMenuItem
              label="Violations"
              icon={faClipboardList}
              route="violations"
              location={location}
            />
          {/*
          <NavMenuItem label="Paper Violations" icon={faFolder} route="paper-violations" />
          <NavMenuItem label="Fleet" icon={faCar} route="fleet" />
          <NavMenuItem label="reports" icon={faChartLine} route="reports" />
          */}
          <NavMenuItem label="Toll Calculator" icon={faRoute} route="toll-calculator" location={location}/>
          </Collapse>
        </List>
        {/*
        <Divider />
        <NavMenuItem label="Help & Support" icon={faQuestion} route="help-support" />
        */}
      </Drawer>
       )}
      </Location>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        <ApplicationRouter userRole={userRole}/>
      </main>
    </div>
  );
}
